import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { Observable, ReplaySubject, Subject, merge, of, timer } from 'rxjs';
import { concatMap, first, startWith, switchMap, tap } from 'rxjs/operators';
import { ProcessPaymentAPIResponse, OrderAPIResponse } from '../../../types/responses';
import { CoreModule } from '../../core.module';
import { AnalyticsService } from '../analytics.service';
import { ServerConnectionService } from '../server-connection.service';
import { UTM_PARAMS, UtmTrackingService } from '../utm-tracking.service';
import { GenreService } from './genre.service';
import { PromoStackPurchaseService } from './promo-stack-purchase.service';
import { UserService } from './user.service';

import { IAttendee, IFeature, IOrder, ISubscription, OrderItem, OrderPromoStacks, Populated } from '../../../types/datatypes';
import { IPopulatedFeature, IPopulatedGenres } from '../../../types/populated';
import { PromoCodeService } from './promo-code.service';
import { ProcessPaymentEvent } from '../../../types/local';

const BASE_ORDER_URI = '/api/order';
const ENDPOINT_ADD_ITEM_TO_CART = `${BASE_ORDER_URI}/add-to-cart`;
const ENDPOINT_CHECK_ORDER_ADVERT_INVENTORY = `${BASE_ORDER_URI}/check-orders-advert-inventory`;
const ENDPOINT_FETCH_ALL_AUTHOR_ORDERS = `${BASE_ORDER_URI}/fetch-all-author-orders`;
export const ENDPOINT_AUTHOR_ORDERS_EXPORT = `${BASE_ORDER_URI}/author-orders-export`;

const ITEM_TYPE_FEATURE = 'featuredBook';
const DEFAULT_PROMOTION_DAY_DIFFERENCE = 30;

const PROMO_TYPE_PERCENT = 'percent';
const PROMO_TYPE_AMOUNT = 'amount';
const PROMO_TYPES = [PROMO_TYPE_PERCENT, PROMO_TYPE_AMOUNT];

interface DataStore {
	order: any;
	editingItemId: string;
}

/**
 * Temp interface to figure out how the inventory management works without going insane.
 */
export interface TempInventoryResponse {
	success: boolean;
	invalidFeatures: string[];
	invalidPromoStacks: { features: string[]; orderPromoStackId: string }[];
	advertsWithInventoryConflict: string[];
	message?: string;
}

export interface ICheckoutOrderData {
	type?: ProcessPaymentEvent['type'];
	orderId: string;
	url: string;

	// Stripe
	messageId?: string;
	stripeCustomerId?: string;
	/**
	 * Payment method used to create a new payment intent
	 */
	paymentMethod?: string;
	/**
	 * Payment intent to confirm if, for example, 3d secure actions are required.
	 */
	paymentIntent?: string;

	/**
	 * @deprecated
	 */
	payPalToken?: any;
	/**
	 * @deprecated
	 */
	payPalTransactionID?: string;

	payPalOrderID?: string;
	savePaymentMethod?: boolean;
}

type Product = OrderPromoStacks | IFeature | IAttendee | ISubscription | IPopulatedFeature;

type OrderValidaionErrorAPIResponse = {
	success: boolean;
	message?: string;
	code?: 'ORDER_COMPLETED' | 'NO_ORDER';
}

@Injectable({
	providedIn: CoreModule,
})
export class OrderService {
	private genres;

	private _orderRefresh = new Subject();

	private _orderStore = new ReplaySubject<Object>(1);

	private _cartUpdating = new ReplaySubject<boolean>();

	private _cartVisibleStatusStore = new ReplaySubject<boolean>(1);

	private _editingItemIdStore = new ReplaySubject<string>(1);

	private _gaOrderItems = [];

	private dataStore: DataStore = {
		order: {},
		editingItemId: null,
	};

	constructor(
		private scs: ServerConnectionService,
		private as: AnalyticsService,
		private utms: UtmTrackingService,
		private us: UserService,
		private promoStackPurchaseService: PromoStackPurchaseService,
		private genreService: GenreService,
		private pcs: PromoCodeService,
	) {
		this.genreService.getGenres().subscribe((genres: IPopulatedGenres[]) => {
			this.genres = genres;
		});
	}

	get orderId() {
		return this.dataStore.order?._id;
	}

	get cartUpdating$() {
		return this._cartUpdating.asObservable();
	}

	get orderStore$() {
		return this._orderStore.asObservable();
	}

	get orderRefresh() {
		return this._orderRefresh.asObservable();
	}

	get cartVisibleStatus$() {
		return this._cartVisibleStatusStore.asObservable();
	}

	get editingItemId$() {
		return this._editingItemIdStore.asObservable();
	}

	public setCartNotVisible() {
		this.setCartVisibleStatus(false);
	}

	public setCartVisible() {
		this.setCartVisibleStatus(true);
	}

	public setEditingItemId(id) {
		this.dataStore.editingItemId = id;
		this.setItemUpdate();
	}

	public clearStoreOrder() {
		this._orderStore.next({});
	}

	public handleWebSocket(data) {
		if (data && data.id && data.action === 'refresh' && data.id === this.dataStore.order?._id) {
			this._orderRefresh.next(data.id);
		}
	}

	getSubscriptionFromOrder() {
		return this.dataStore.order.items.find((item) => item.itemType === 'subscription');
	}

	public clearOrder() {
		this.clearStoreOrder();
	}

	public addUserToOrder(userId: string) {
		return this.scs.http$('POST', `${BASE_ORDER_URI}/add-user`, null, { userId })
			.pipe(tap((result: any) => {
				// If a guest order was merged with an existing user order, we need to load the user order.
				if (result.merged) {
					this.fetchOrder();
				}
			}));
	}

	public updateOrderDataStore(order) {
		this.dataStore.order = order;
		this.setOrder();
		this.setEditingItemId(null);
	}

	public removeSubscriptionFromCart() {
		const subscription = this.getSubscriptionFromOrder();
		if (!subscription) {
			return of(null);
		}

		return this.removeItem(subscription.itemId);
	}

	public applyMemberDiscount(userId: string) {
		// Wait for the order to be merged, then apply the member discount.
		// There might be cases where order doesnt get merged?

		const timeLimit$ = timer(5 * 1000).pipe(startWith(0));

		return merge(this.orderStore$, timeLimit$)
			.pipe(first())
			.pipe(switchMap(() => {
				const body = {
					userId,
				};

				return this.scs.http$('POST', '/api/order/apply-member-discount', null, body);
			})).pipe(concatMap((result: any) => {
				if (result.success) {
					this.pcs.triggerUpdatePromoCode();
					this.refreshCart();
					return of(result);
				}

				return of(null);
			}));
	}

	public addItem(
		id: string,
		type: 'event' | 'featuredBook' | 'promoStack' | 'subscription',
		item: Product,
	) {
		const payload: { itemId: string; itemType: string; itemData?: any; user?: string } = {
			itemId: id,
			itemType: type,
			user: this.us.loggedIn && this.us?.me?._id ? this.us.me._id : null,
		};
		return this.scs.post$<OrderAPIResponse>('/api/order/add-item', payload)
			.pipe(tap((result: any) => {
				if (result.success) {
					this.updateOrderDataStore(result.order);
					this.refreshCart();
					this.handleAddToCartAnalytics(type, item);
				} else {
					this._handleCompletedOrMissingOrderResponse(result);
				}
			}));
	}

	public removeItem(id: string) {
		return this.scs.post$<OrderAPIResponse>('/api/order/remove-item', {
			itemId: id,
		}).pipe(tap((result) => {
			if (result.success) {
				const item = (this.dataStore.order as IOrder).items.find((orderItem) => orderItem._id === id);
				if (item) {
					this.handleRemoveFromCartAnalytics(item);
				}
			} else {
				this._handleCompletedOrMissingOrderResponse(result);
			}
		}));
	}

	/**
	 * Sends order info for payment processing and completes the order.
	 */
	public processPayment(orderDetails: ICheckoutOrderData) {
		const utmTracking = this.buildUTMTrackingParams();
		const payload = { ...orderDetails, tracking: utmTracking };
		return this.scs.post$<ProcessPaymentAPIResponse>('/api/order/process-payment', payload);
	}

	public addSubscriptionToCart(planId, userInfo) {
		if (!this.us?.me?._id) {
			return this.us.createTempUser(userInfo)
				.pipe(concatMap((tempUser) => this.scs.http$('POST', '/api/order/add-subscription-to-cart', null, {
					planId,
					userId: null,
					tempUser: tempUser.user,
				})));
		}

		const body = {
			planId,
			userId: this.us?.me?._id,
		};
		return this.scs.http$('POST', '/api/order/add-subscription-to-cart', null, body);
	}

	public addPromoStackToCart(id) {
		const { purchaserInfo } = this.promoStackPurchaseService;

		return new Promise((resolve) => this.scs.http$(
			'POST',
			'/api/order/add-promo-stack-to-cart',
			null,
			{ promoStackId: id, userId: this.us?.me?._id, purchaserInfo },
		).subscribe((data: any) => {
			this.updateOrderDataStore(data.order);
			resolve(data);
		}));
	}

	public addToCart(featuredBook, user, mongoId = null) {
		this._cartUpdating.next(true);
		const featuredBookData = {
			...featuredBook,
			// Pretty sure if this is null the implication is that it needs to be created
			itemMongoId: mongoId || this.dataStore.editingItemId,
		};
		featuredBookData._owner = user && user._id ? user._id : null;
		featuredBookData.purchaserInfo.loggedIn = !!((user && user._id));
		return new Promise((resolve, reject) => {
			this.scs.http$('POST', ENDPOINT_ADD_ITEM_TO_CART, null, featuredBookData).subscribe(
				(result: any) => {
					if (result.success && result.order) {
						this.updateOrderDataStore(result.order);
						const { _site, _siteAdvert, _book, purchaseSource } = featuredBook;
						// Can be deprecated after GA4 is implemented and good to go.
						const listName = `${_site.name} for Authors`;
						const productData = {
							id: _siteAdvert._id,
							name: `${_site.name} ${_siteAdvert.name} Feature`,
							category: `${_site.name} / ${_siteAdvert.name}`,
							quantity: 1,
							price: `${_siteAdvert.price}`,
							dimension1: _book.author,
							dimension2: user ? user._id : '',
						};
						this.as.trackAddToCart(listName, productData);
						// End of deprecated code.

						const { seoSlug, name, parentGenre } = _siteAdvert;
						const ecommerceItem = this.as.generateAnalyticsEcommerceObject(
							seoSlug,
							name,
							_site.name,
							parentGenre,
							featuredBook.paymentAmount,
							1,
							purchaseSource || _site.name,
							purchaseSource,
						);

						this.as.trackAddToCart2(featuredBook.paymentAmount, [ecommerceItem]);
						this._cartUpdating.next(false);
						resolve(result);
						this.pcs.triggerUpdatePromoCode();
					} else {
						reject(result);
					}
				},
			);
		});
	}

	_checkForMatchingExistingItem(item) {
		const { _id } = item;

		if (!this.dataStore?.order?.items) {
			return false;
		}

		const duplicate = this.dataStore.order.items.find((orderItem) => {
			const { item: orderItemData } = orderItem;
			return orderItemData._id === _id;
		});

		return !!duplicate;
	}

	handlePromoStackRemoveAnalytics(promoStack) {
		const ecommerceItem = this.as.generateAnalyticsEcommerceObject(
			promoStack.name,
			promoStack.name,
			'Various',
			undefined,
			promoStack.price,
			1,
			'Promo Stacks',
			'Promo Stacks',
		);

		this.as.trackRemoveFromCart2(null, ecommerceItem, 'Promo Stacks');
	}

	handleFeatureRemoveAnalytics(featuredBook, userId) {
		const { _site, _siteAdvert, paymentAmount } = featuredBook;

		const ecommerceItem = this.as.generateAnalyticsEcommerceObject(
			_siteAdvert.seoSlug,
			_siteAdvert.name,
			_site.name,
			_siteAdvert.parentGenre,
			paymentAmount,
			1,
		);

		this.as.trackRemoveFromCart2(null, ecommerceItem, _site.seoSlug);
	}

	public parseAdvertInventoryConflictError(rawConflictData, order) {
		// Default values for the error object
		const errorObject = {
			text: 'Something went wrong. Please try again later.',
			title: 'Whoops!',
		};

		if (rawConflictData && rawConflictData.length && order) {
			const conflictData = rawConflictData[0];
			if (
				conflictData
				&& conflictData._siteAdvert
				&& conflictData.cartFeatureCountForSiteAdvert
				// Only check for this key on the object since value could be zero
				// eslint-disable-next-line no-prototype-builtins
				&& conflictData.hasOwnProperty('availableInventory')
			) {
				const {
					_siteAdvert,
					cartFeatureCountForSiteAdvert,
					availableInventory,
				} = conflictData;
				const featuresToMove = cartFeatureCountForSiteAdvert - availableInventory;
				const isSingle = availableInventory === 1;
				const featureWithConflictAdvert = order.items.filter(({ item }) => item?._siteAdvert?._id === _siteAdvert)[0];

				if (featureWithConflictAdvert) {
					const {
						item: {
							_siteAdvert: { name: siteAdvertName = '' } = {},
							_site: { name: siteName = '' } = {},
						},
					} = featureWithConflictAdvert;
					errorObject.text = `There are ${cartFeatureCountForSiteAdvert} features in your cart for promotion type 
					${siteName} ${siteAdvertName} on the same promo date. But there ${isSingle ? 'is' : 'are'} 
					only ${availableInventory} ${isSingle ? 'spot' : 'spots'} left. Please move at 
					least ${featuresToMove} ${featuresToMove === 1 ? 'feature' : 'features'} to a new promotion date.`;
				} else {
					const promoStackWithConflict = order.items.filter(({ item }) => item._promoStack?.siteAdverts.includes(_siteAdvert))[0];

					if (promoStackWithConflict) {
						const {
							item: { _promoStack: { name: promoStackName = '' } = {} },
						} = promoStackWithConflict;
						errorObject.text = `There are ${cartFeatureCountForSiteAdvert} promo stacks in your cart for  
						${promoStackName} on the same promo date. But there ${isSingle ? 'is' : 'are'} only ${availableInventory} 
						${isSingle ? 'spot' : 'spots'} left. Please move at least ${featuresToMove} 
						${featuresToMove === 1 ? 'feature' : 'features'} to a new promotion date.`;
					}
				}
			}
		}
		return errorObject;
	}

	public fetchOrder() {
		return this.scs.http$('GET', '/api/order/', null, null).subscribe((result: any) => {
			if (result.success) {
				this.dataStore.order = result.order;
				this.setOrder();
			}
		});
	}

	public fetchOrderByID(orderId: string) {
		return this.scs.http$('GET', `/api/order/${orderId}`, null, null);
	}

	public refreshCart() {
		return this.fetchOrder();
	}

	public checkOrderForThirtyDayViolation(order) {
		let error = null;
		if (order && order.items) {
			order.items.map(({ item: currentItem, itemType: currentItemType }) => {
				if (currentItemType === ITEM_TYPE_FEATURE) {
					const feature = currentItem;
					const duplicateFeatures = order.items.filter(({ item, itemType }) => {
						if (itemType === ITEM_TYPE_FEATURE) {
							const { _book, _siteAdvert } = item;
							return (
								_book._id === feature._book._id
								&& _siteAdvert._id === feature._siteAdvert._id
							);
						}

						return false;
					});
					if (duplicateFeatures.length === 2) {
						const firstPubDate = moment(duplicateFeatures[0].item.pubDate, 'YYYYMMDD');
						const secondPubDate = moment(duplicateFeatures[1].item.pubDate, 'YYYYMMDD');
						const daysBetweenPubDates = Math.abs(firstPubDate.diff(secondPubDate, 'days'));
						if (daysBetweenPubDates <= DEFAULT_PROMOTION_DAY_DIFFERENCE) {
							error = {
								errorText: 'Your promotions are too close together.'
									+ ' Please make sure there are more than 30 days between your promotions.',
							};
						}
					}
				}

				return null;
			});
		}
		return error;
	}

	/**
	 * @deprecated
	 */
	public checkOrderSiteInventory(orderId: string): Observable<TempInventoryResponse> {
		return this.scs.http$('GET', `${ENDPOINT_CHECK_ORDER_ADVERT_INVENTORY}/${orderId}`, null, null) as Observable<TempInventoryResponse>;
	}

	public fetchAllAuthorOrders(userId) {
		return this.scs.http$('GET', `${ENDPOINT_FETCH_ALL_AUTHOR_ORDERS}/${userId}`, null, null);
	}

	public initiateCheckout() {
		return this.scs.http$('GET', '/api/order/initiate-checkout/', null, null);
	}

	public calculateOrderTotal() {
		const { order } = this.dataStore;

		return order?.total;
	}

	/**
   * Helper function to retrieve and build the saved UTM params from the purchaser's cookies
   */
	private buildUTMTrackingParams() {
		const utmTracking = {};
		UTM_PARAMS.forEach((utmParam) => {
			const utmValue = this.utms.getUTMParamFromCookie(utmParam);
			if (utmValue) {
				utmTracking[utmParam] = utmValue;
			}
		});
		return utmTracking;
	}

	private handleAddToCartAnalytics(itemType: OrderItem['itemType'], item: Product) {
		if (itemType === 'event') {
			const attendee = (item as Populated<IAttendee, '_event'>);
			const event = attendee._event;
			const eventDateFormat = moment(event.time.start).format('YYYY-MM-DD');
			const ecommerceItem = this.as.generateAnalyticsEcommerceObject(
				event._id,
				`${eventDateFormat} ${event.name}`,
				null,
				null,
				attendee.paymentAmount,
				1,
				'Events',
				'Events',
			);
			this.as.trackAddToCart2(attendee.paymentAmount, [ecommerceItem]);
		}
		if (itemType === 'featuredBook') {
			const feature = (item as Populated<IFeature, '_site' | '_siteAdvert'>);
			const { seoSlug, name, parentGenre } = feature._siteAdvert;
			const ecommerceItem = this.as.generateAnalyticsEcommerceObject(
				seoSlug,
				name,
				feature._site.name,
				parentGenre,
				feature.paymentAmount,
				1,
				feature.purchaseSource || feature._site.name,
				feature.purchaseSource,
			);
			this.as.trackAddToCart2(feature.paymentAmount, [ecommerceItem]);
		}
		if (itemType === 'promoStack') {
			const orderItem = (item as OrderPromoStacks);
			const promoStack = orderItem._promoStack;
			const ecommerceItem = this.as.generateAnalyticsEcommerceObject(
				promoStack.name,
				promoStack.name,
				'Various',
				undefined,
				promoStack.price,
				1,
				'Promo Stacks',
				'Promo Stacks',
			);
			this.as.trackAddToCart2(orderItem.paymentAmount, [ecommerceItem]);
		}
	}

	private handleRemoveFromCartAnalytics(item: OrderItem) {
		if (item.itemType === 'event') {
			const attendee = item.item;
			const eventDateFormat = moment(attendee.date).format('YYYY-MM-DD');
			const ecommerceItem = this.as.generateAnalyticsEcommerceObject(
				attendee.eventId,
				`${eventDateFormat} ${attendee.name}`,
				null,
				null,
				attendee.paymentAmount,
				1,
				'Events',
				'Events',
			);
			this.as.trackRemoveFromCart2(attendee.paymentAmount, ecommerceItem);
		}
		if (item.itemType === 'featuredBook') {
			const feature = item.item;
			const { _siteAdvert, _site } = feature;
			const ecommerceItem = this.as.generateAnalyticsEcommerceObject(
				_siteAdvert.seoSlug,
				_siteAdvert.name,
				_site.name,
				_siteAdvert.parentGenre,
				feature.paymentAmount,
				1,
			);
			this.as.trackRemoveFromCart2(feature.paymentAmount, ecommerceItem, _site.seoSlug);
		}
		if (item.itemType === 'promoStack') {
			const promoStack = item.item._promoStack;
			const ecommerceItem = this.as.generateAnalyticsEcommerceObject(
				promoStack.name,
				promoStack.name,
				'Various',
				undefined,
				promoStack.price,
				1,
				'Promo Stacks',
				'Promo Stacks',
			);

			this.as.trackRemoveFromCart2(null, ecommerceItem, 'Promo Stacks');
		}
	}

	private setOrder() {
		this._orderStore.next({ ...this.dataStore.order });
	}

	private setCartVisibleStatus(status) {
		this._cartVisibleStatusStore.next(status);
	}

	private setItemUpdate() {
		this._editingItemIdStore.next(this.dataStore.editingItemId);
	}

	private totalFeature(feature) {
		const featureHasValidPromo = this.featureHasValidPromoCode(feature);
		const featureHasValidAdvert = this.featureHasValidSiteAdvert(feature);
		const { _siteAdvert } = feature;

		if (featureHasValidPromo && featureHasValidAdvert) {
			// Feature has a promo code, so we take that into account for the order's totalAmount
			return _siteAdvert.price - feature.discountAmount;
		}
		if (featureHasValidAdvert) {
			// No promo code, just add the original siteAdvert price
			return _siteAdvert.price;
		}

		return 0;
	}

	private featureHasValidPromoCode(feature) {
		return (
			feature
			&& feature._promoCode
			&& feature._promoCode.discountType
			&& PROMO_TYPES.includes(feature._promoCode.discountType)
		);
	}

	private featureHasValidSiteAdvert(feature) {
		return (
			feature
			&& feature._siteAdvert
			&& feature._siteAdvert.price
		);
	}

	private _handleCompletedOrMissingOrderResponse(result: OrderValidaionErrorAPIResponse): void {
		console.log(result);
		// The order is completed, or doesnt exist anymore, so we need to clear the data store.
		// We should then send the user a nice message, but we will have the subscriber handle that.
		if (result.success === false && ['ORDER_COMPLETED', 'NO_ORDER'].includes(result.code)) {
			this.updateOrderDataStore({
				order: {},
				editingItemId: null,
			});
		}
	}
}
